import { baseApi } from 'services/api';
import { API_BASE_URL, API_TAG_TYPES, API_VERSION, JOB_MANAGER_API_HEADERS } from 'constants/common';
import { downloadFile } from 'utils/commonUtils';
import { FileTypes } from 'types/baseTypes';
import {
  ExportPayRunMemberJobsRequest,
  GetJobCommissionExplainRequest,
  GetJobCommissionExplainResponse,
  GetPayRunMemberJobsRequest,
  GetPayRunMemberJobsResponse,
  UpdateBulkJobStatusRequest,
  UpdateBulkJobStatusResponse,
  UpdateJobStatusRequest,
  UpdateJobStatusResponse
} from './confirmJob.model';
import { getUrlSearchParams } from 'utils/apiUtils';

export const confirmJobsApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getPayRunMemberJobs: builder.query<GetPayRunMemberJobsResponse, GetPayRunMemberJobsRequest>({
      query: ({ teamId, payRunId, merchantId, jobReviewStatus, ...params }) => {
        const searchParams = getUrlSearchParams(params);
        const statusParam = jobReviewStatus?.length ? `&jobReviewStatus=${jobReviewStatus.join(',')}` : '';

        return {
          // eslint-disable-next-line max-len
          url: `${API_VERSION.V1}/merchants/teams/${teamId}/pay-run/${payRunId}/merchants/${merchantId}/jobs?${searchParams}${statusParam}`,
          method: 'GET'
        };
      },
      providesTags: [API_TAG_TYPES.PAY_RUN_MEMBER_JOBS],
      extraOptions: {
        failure: 'Failed to fetch pay run member jobs',
        showToast: true
      }
    }),

    exportPayRunMemberJobs: builder.query<Blob, ExportPayRunMemberJobsRequest>({
      query: ({ teamId, payRunId, merchantId, jobReviewStatus, ...params }) => {
        const searchParams = getUrlSearchParams(params);
        const statusParam = jobReviewStatus?.length ? `&jobReviewStatus=${jobReviewStatus.join(',')}` : '';

        let url = `${API_VERSION.V1}/merchants/teams/${teamId}/pay-run/${payRunId}/merchants/${merchantId}/jobs/export`;
        if (searchParams || statusParam) {
          url += `?${searchParams}${statusParam}`;
        }

        return {
          url,
          method: 'GET',
          responseHandler: async response => {
            await downloadFile({
              response,
              fileType: FileTypes.CSV,
              fileName: 'pay-run-member-jobs-export'
            });
          }
        };
      },
      extraOptions: {
        failure: 'Failed to export pay run member jobs',
        success: 'Pay run member jobs exported successfully',
        showToast: true
      }
    }),

    updateJobStatus: builder.mutation<UpdateJobStatusResponse, UpdateJobStatusRequest>({
      query: ({ teamId, payRunId, merchantId, status, jobId }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/pay-run/${payRunId}/merchant/job/status`,
        method: 'PATCH',
        body: {
          merchantId,
          status,
          jobId
        }
      }),
      invalidatesTags: [API_TAG_TYPES.PAY_RUN_MEMBER_DETAILS, API_TAG_TYPES.PAY_RUN_MEMBER_JOBS],
      extraOptions: {
        failure: 'Failed to update job status',
        showToast: true,
        success: 'Job status updated successfully'
      }
    }),

    updateBulkJobStatus: builder.mutation<UpdateBulkJobStatusResponse, UpdateBulkJobStatusRequest>({
      query: ({ teamId, payRunId, merchantId, status, jobIds }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/pay-run/${payRunId}/merchant/bulk-job-status`,
        method: 'PATCH',
        body: {
          merchantId,
          status,
          jobIds
        }
      }),
      invalidatesTags: [API_TAG_TYPES.PAY_RUN_MEMBER_DETAILS, API_TAG_TYPES.PAY_RUN_MEMBER_JOBS],
      extraOptions: {
        failure: 'Failed to update job statuses',
        showToast: true,
        success: 'Job statuses updated successfully'
      }
    }),
    getJobCommissionExplain: builder.query<GetJobCommissionExplainResponse, GetJobCommissionExplainRequest>({
      query: ({ teamId, jobId, merchantId }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/commission-explain`,
        method: 'POST',
        body: {
          merchantId
        },
        headers: JOB_MANAGER_API_HEADERS
      })
    })
  })
});

export const {
  useGetPayRunMemberJobsQuery,
  useLazyGetPayRunMemberJobsQuery,
  useUpdateJobStatusMutation,
  useUpdateBulkJobStatusMutation,
  useGetJobCommissionExplainQuery,
  useLazyExportPayRunMemberJobsQuery
} = confirmJobsApi;
